import React, { useEffect, useMemo, useState } from "react";
import "../../styles/glass.css";
import "../../styles/globals.css";
import "../../styles/layout.css";
import "../styles/platform.css";
import "../styles/LeftSidebar.css";
import HeaderPlatform from "../components/HeaderPlatform";
import FooterPlatform from "../components/FooterPlatform";
import LeftSidebar from "../components/LeftSidebar";
import GlassCard from "../../components/GlassCard";
import { useCurrentLang } from "../../hooks/useCurrentLang";
import { ExerciseBlock } from "../components/ExerciseBlock";
import { unknownExercisesText } from "../data/platformText";
import { getUnknownExercises, checkUnknownExercise } from "../api/unknownExercisesApi";

export const UnknownExercisesPage = () => {
  const currentLang = useCurrentLang();
  const t = unknownExercisesText[currentLang];

  const [rows, setRows] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadUnknownExercises() {
      try {
        setLoading(true);
        setError("");
        const data = await getUnknownExercises();
        setRows(data || []);
      } catch (err) {
        setError(err.message || "Failed to load unknown exercises");
      } finally {
        setLoading(false);
      }
    }

    loadUnknownExercises();
  }, []);

  const exercises = useMemo(() => {
    const parseCsvToArray = (value) => {
      if (!value) return [];
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    };

    return rows.map((row) => {
      const exercise = row.exercise || {};

      return {
        id: row.id,
        exerciseId: row.exercise_id,
        name: exercise.name ?? "Unnamed exercise",
        difficulty: exercise.difficulty ?? "-",
        inSession: false,
        muscles: [
          ...parseCsvToArray(exercise.primary_muscles),
          ...parseCsvToArray(exercise.secondary_muscles),
        ],
        longDescription: exercise.long_description ?? "",
        detailImages: exercise.detail_images_url ? [exercise.detail_images_url] : [],
        detailMusclesImageUrl: exercise.detail_muscles_images_url ?? "",
        detailYoutubeUrl: exercise.detail_youtube_url ?? "",
        commonMistakes: parseCsvToArray(exercise.common_mistakes),
        primaryMuscles: exercise.primary_muscles ?? "",
        secondaryMuscles: exercise.secondary_muscles ?? "",
        body_region: exercise.body_region ?? [],
      };
    });
  }, [rows]);

  const handleToggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleCompleteExercise = async (id) => {
    try {
      await checkUnknownExercise(id);
      setRows((prev) => prev.filter((row) => row.id !== id));
      setExpandedId((prev) => (prev === id ? null : prev));
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to check exercise");
    }
  };

  return (
    <div className="revimo-layout">
      <HeaderPlatform />
      <main className="revimo-main">
        <LeftSidebar />
        <div className="revimo-page-content">
          <GlassCard style={{ padding: "20px", maxWidth: "775px", marginLeft: "50px" }}>
            <div className="profile-section header-section">
              <div className="profile-col profile-col-left">
                <div
                  style={{
                    fontSize: "0.8rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    opacity: 0.7,
                  }}
                >
                  {t.s}
                </div>

                <div style={{ textAlign: "start", paddingBottom: "5px", fontWeight: "500" }}>
                  <div style={{ margin: "5px", fontWeight: "600", fontSize: "26px", maxWidth: "500px" }}>
                    {t.t1}
                  </div>
                  <div>{t.t2}</div>
                  <div>{t.t3}</div>
                </div>
              </div>
            </div>

            <div style={{ height: "20px" }}></div>
            <div style={{ color: "var(--text-muted)" }}>{t.desc}</div>
          </GlassCard>

          <GlassCard
            style={{
              padding: "10px",
              maxWidth: "775px",
              marginLeft: "50px",
              marginTop: "10px",
              marginBottom: "10px",
            }}
          >
            {loading && <div style={{ padding: "12px" }}>Loading unknown exercises...</div>}

            {error && <div style={{ padding: "12px", color: "#ff6b6b" }}>{error}</div>}

            {!loading && !error && exercises.length === 0 && (
              <div style={{ padding: "12px", color: "var(--text-muted)" }}>
                {t.noNew}
              </div>
            )}

            {!loading &&
              !error &&
              exercises.map((ex) => (
                <ExerciseBlock
                  key={ex.id}
                  {...ex}
                  option="newExercise"
                  isExpanded={expandedId === ex.id}
                  onToggleExpand={handleToggleExpand}
                  onCompleteExercise={handleCompleteExercise}
                  l_m="0px"
                />
              ))}
          </GlassCard>
        </div>
      </main>

      <footer className="revimo-footer">
        <FooterPlatform />
      </footer>
    </div>
  );
};

export default UnknownExercisesPage;